// frontend/src/components/Events/EventCalendarModal.js
// iOS 26 / Glass redesign + month cache + non-blocking loading.
// Existing calendar mechanics are preserved: month navigation, day selection,
// and event panel expansion directly below the selected week.

import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Box,
  Typography,
  Chip,
  CircularProgress,
  Alert,
  alpha,
  useTheme,
  Collapse,
} from '@mui/material';
import {
  Close as CloseIcon,
  ChevronLeftRounded as ChevronLeftIcon,
  ChevronRightRounded as ChevronRightIcon,
  EventRounded as EventIcon,
  KeyboardArrowUpRounded as CollapseIcon,
  OpenInNewRounded as OpenInNewIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import api from '../../services/api';
import { getEventTypePalette } from './eventTypePalette';

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-container': {
    padding: theme.spacing(2),
  },
  '& .MuiDialog-paper': {
    width: 'min(800px, calc(100vw - 32px))',
    maxWidth: 800,
    minWidth: 0,
    maxHeight: 'min(90vh, 820px)',
    margin: 0,
    overflow: 'hidden',
    borderRadius: theme.spacing(4.5),
    border: `1px solid ${alpha(theme.palette.common.white, 0.84)}`,
    background: `
      linear-gradient(145deg,
        ${alpha(theme.palette.common.white, 0.80)},
        ${alpha(theme.palette.grey[100], 0.52)} 58%,
        ${alpha(theme.palette.primary.main, 0.045)}
      ),
      radial-gradient(circle at 94% 4%, ${alpha(theme.palette.primary.main, 0.10)}, transparent 34%),
      radial-gradient(circle at 8% 96%, ${alpha(theme.palette.info.main, 0.055)}, transparent 34%)
    `,
    backgroundColor: alpha(theme.palette.common.white, 0.62),
    backdropFilter: 'blur(28px) saturate(145%)',
    WebkitBackdropFilter: 'blur(28px) saturate(145%)',
    boxShadow: `0 28px 90px ${alpha(theme.palette.common.black, 0.22)}`,
  },
  '& .MuiBackdrop-root': {
    backgroundColor: alpha(theme.palette.common.black, 0.32),
  },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
  padding: theme.spacing(2.1, 2.5),
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  flexShrink: 0,
  background: `linear-gradient(135deg,
    ${alpha(theme.palette.common.white, 0.66)},
    ${alpha(theme.palette.common.white, 0.20)}
  )`,
  borderBottom: `1px solid ${alpha(theme.palette.common.white, 0.78)}`,
}));

const ContentSurface = styled(DialogContent)(({ theme }) => ({
  flex: 1,
  minHeight: 0,
  overflowY: 'auto',
  padding: theme.spacing(2.3, 2.5, 2.6),
  background: `linear-gradient(180deg,
    ${alpha(theme.palette.common.white, 0.14)},
    ${alpha(theme.palette.primary.main, 0.018)}
  )`,
  '&::-webkit-scrollbar': { width: 7 },
  '&::-webkit-scrollbar-track': {
    background: alpha(theme.palette.common.white, 0.22),
    borderRadius: 999,
  },
  '&::-webkit-scrollbar-thumb': {
    background: alpha(theme.palette.text.secondary, 0.18),
    borderRadius: 999,
  },
}));

const CalendarSurface = styled(Box)(({ theme }) => ({
  borderRadius: theme.spacing(3),
  padding: theme.spacing(1.35),
  border: `1px solid ${alpha(theme.palette.common.white, 0.78)}`,
  background: `linear-gradient(145deg,
    ${alpha(theme.palette.common.white, 0.48)},
    ${alpha(theme.palette.common.white, 0.20)}
  )`,
  boxShadow: `0 10px 34px ${alpha(theme.palette.common.black, 0.045)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.88)}`,
}));

const WeekdayGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 5,
  marginBottom: 6,
});

const CalendarGrid = styled(Box)({
  display: 'grid',
  gridTemplateColumns: 'repeat(7, 1fr)',
  gap: 5,
});

const DayCell = styled(Box, {
  shouldForwardProp: (prop) => !['isToday', 'hasEvents', 'isSelected', 'isWeekend'].includes(prop),
})(({ theme, isToday, hasEvents, isSelected, isWeekend }) => ({
  minWidth: 0,
  aspectRatio: '1',
  borderRadius: theme.spacing(2.1),
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  position: 'relative',
  cursor: hasEvents ? 'pointer' : 'default',
  color: isWeekend ? alpha(theme.palette.text.primary, 0.68) : theme.palette.text.primary,
  background: isSelected
    ? `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.14)}, ${alpha(theme.palette.common.white, 0.40)})`
    : isToday
      ? `linear-gradient(145deg, ${alpha(theme.palette.common.white, 0.54)}, ${alpha(theme.palette.primary.main, 0.055)})`
      : alpha(theme.palette.common.white, 0.16),
  border: isSelected
    ? `1px solid ${alpha(theme.palette.primary.main, 0.58)}`
    : isToday
      ? `1px solid ${alpha(theme.palette.primary.main, 0.34)}`
      : `1px solid ${alpha(theme.palette.common.white, 0.34)}`,
  boxShadow: isSelected
    ? `0 7px 18px ${alpha(theme.palette.primary.main, 0.10)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.86)}`
    : `inset 0 1px 0 ${alpha(theme.palette.common.white, 0.58)}`,
  transition: 'transform 180ms ease, background-color 180ms ease, border-color 180ms ease, box-shadow 180ms ease',
  '&:hover': hasEvents ? {
    transform: 'translateY(-1px)',
    borderColor: alpha(theme.palette.primary.main, 0.40),
    background: `linear-gradient(145deg, ${alpha(theme.palette.primary.main, 0.085)}, ${alpha(theme.palette.common.white, 0.40)})`,
    boxShadow: `0 7px 20px ${alpha(theme.palette.primary.main, 0.08)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.86)}`,
  } : {},
}));

const EventDots = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: 3,
  minHeight: 7,
  maxWidth: '76%',
  marginTop: 4,
});

const EventDot = styled(Box)(({ color }) => ({
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: color,
  boxShadow: `0 1px 4px ${alpha(color || '#000', 0.24)}`,
  flexShrink: 0,
}));

const MonthNavigator = styled(Box)(({ theme }) => ({
  display: 'inline-flex',
  alignItems: 'center',
  gap: theme.spacing(0.7),
  padding: theme.spacing(0.55),
  borderRadius: 999,
  border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
  background: `linear-gradient(145deg,
    ${alpha(theme.palette.common.white, 0.62)},
    ${alpha(theme.palette.common.white, 0.28)}
  )`,
  boxShadow: `0 8px 24px ${alpha(theme.palette.common.black, 0.045)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.92)}`,
}));

const MonthButton = styled(IconButton)(({ theme }) => ({
  width: 34,
  height: 34,
  color: theme.palette.text.secondary,
  border: `1px solid ${alpha(theme.palette.common.white, 0.66)}`,
  backgroundColor: alpha(theme.palette.common.white, 0.26),
  '&:hover': {
    color: theme.palette.primary.main,
    backgroundColor: alpha(theme.palette.primary.main, 0.08),
  },
}));

const DayEventsPanel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.35, 1.45),
  borderRadius: theme.spacing(2.7),
  marginTop: theme.spacing(0.8),
  border: `1px solid ${alpha(theme.palette.common.white, 0.76)}`,
  background: `linear-gradient(145deg,
    ${alpha(theme.palette.common.white, 0.54)},
    ${alpha(theme.palette.common.white, 0.22)}
  ), radial-gradient(circle at 100% 0%, ${alpha(theme.palette.primary.main, 0.075)}, transparent 45%)`,
  boxShadow: `0 10px 30px ${alpha(theme.palette.common.black, 0.05)}, inset 0 1px 0 ${alpha(theme.palette.common.white, 0.88)}`,
}));

const EventItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'typeColor',
})(({ theme, typeColor }) => ({
  padding: theme.spacing(0.95, 1.1, 0.95, 1.25),
  marginBottom: theme.spacing(0.55),
  borderRadius: theme.spacing(1.8),
  border: `1px solid ${alpha(theme.palette.common.white, 0.58)}`,
  borderLeft: `3px solid ${typeColor || theme.palette.primary.main}`,
  background: `linear-gradient(145deg,
    ${alpha(typeColor || theme.palette.primary.main, 0.075)},
    ${alpha(theme.palette.common.white, 0.25)}
  )`,
  cursor: 'pointer',
  transition: 'transform 160ms ease, background-color 160ms ease, box-shadow 160ms ease',
  '&:last-child': { marginBottom: 0 },
  '&:hover': {
    transform: 'translateX(2px)',
    background: `linear-gradient(145deg,
      ${alpha(typeColor || theme.palette.primary.main, 0.12)},
      ${alpha(theme.palette.common.white, 0.34)}
    )`,
    boxShadow: `0 6px 18px ${alpha(typeColor || theme.palette.primary.main, 0.07)}`,
  },
}));

const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс'];

const MONTHS = [
  'Январь', 'Февраль', 'Март', 'Апрель', 'Май', 'Июнь',
  'Июль', 'Август', 'Сентябрь', 'Октябрь', 'Ноябрь', 'Декабрь'
];

const MAX_DOTS = 5;

const pluralizeEvents = (count) => {
  const mod10 = count % 10;
  const mod100 = count % 100;
  if (mod10 === 1 && mod100 !== 11) return `${count} событие`;
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return `${count} события`;
  return `${count} событий`;
};

const getMonthKey = (year, month) =>
  `${year}-${String(month + 1).padStart(2, '0')}`;

const EventCalendarModal = ({ open, onClose }) => {
  const theme = useTheme();
  const today = useMemo(() => new Date(), []);
  const requestIdRef = useRef(0);

  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [calendarCache, setCalendarCache] = useState({});
  const [loadingMonth, setLoadingMonth] = useState(null);
  const [error, setError] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);

  const monthKey = getMonthKey(currentYear, currentMonth);
  const cachedMonth = calendarCache[monthKey];
  const eventsByDay = cachedMonth?.days || {};
  const isInitialLoading = !cachedMonth && loadingMonth === monthKey;

  useEffect(() => {
    if (!open) return;

    const loadMonth = async () => {
      const key = getMonthKey(currentYear, currentMonth);
      const requestId = ++requestIdRef.current;
      const hasCache = Boolean(calendarCache[key]);

      setError(null);
      setLoadingMonth(key);

      try {
        const response = await api.get('/conferences/events/calendar/', {
          params: {
            year: currentYear,
            month: currentMonth + 1,
          },
        });

        if (requestId !== requestIdRef.current) return;

        setCalendarCache((prev) => ({
          ...prev,
          [key]: {
            days: response.data?.days || {},
            totalEvents: response.data?.total_events || 0,
            loadedAt: Date.now(),
          },
        }));
      } catch (err) {
        if (requestId !== requestIdRef.current) return;

        console.error('❌ Ошибка загрузки календаря:', err);
        if (!hasCache) {
          setError('Не удалось загрузить данные');
        }
      } finally {
        if (requestId === requestIdRef.current) {
          setLoadingMonth(null);
        }
      }
    };

    loadMonth();
  }, [open, currentMonth, currentYear]);

  const daysInMonth = useMemo(
    () => new Date(currentYear, currentMonth + 1, 0).getDate(),
    [currentYear, currentMonth]
  );

  const firstDayOfWeek = useMemo(
    () => new Date(currentYear, currentMonth, 1).getDay() || 7,
    [currentYear, currentMonth]
  );

  const calendarDays = useMemo(() => {
    const days = [];
    for (let i = 1; i < firstDayOfWeek; i += 1) days.push(null);
    for (let day = 1; day <= daysInMonth; day += 1) days.push(day);
    return days;
  }, [daysInMonth, firstDayOfWeek]);

  const weeks = useMemo(() => {
    const result = [];
    for (let i = 0; i < calendarDays.length; i += 7) {
      result.push(calendarDays.slice(i, i + 7));
    }
    return result;
  }, [calendarDays]);

  const handlePrevMonth = () => {
    setSelectedDay(null);
    if (currentMonth === 0) {
      setCurrentMonth(11);
      setCurrentYear((prev) => prev - 1);
    } else {
      setCurrentMonth((prev) => prev - 1);
    }
  };

  const handleNextMonth = () => {
    setSelectedDay(null);
    if (currentMonth === 11) {
      setCurrentMonth(0);
      setCurrentYear((prev) => prev + 1);
    } else {
      setCurrentMonth((prev) => prev + 1);
    }
  };

  const isToday = (day) => (
    day === today.getDate() &&
    currentMonth === today.getMonth() &&
    currentYear === today.getFullYear()
  );

  const getDayEvents = (day) => eventsByDay[String(day)] || [];

  const handleDayClick = (day) => {
    if (!day || getDayEvents(day).length === 0) return;
    setSelectedDay((prev) => (prev === day ? null : day));
  };

  const handleEventClick = (eventId) => {
    window.open(`https://fincollab.ru/events/${eventId}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <StyledDialog open={open} onClose={onClose} maxWidth={false} fullWidth={false}>
      <StyledDialogTitle>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1, sm: 1.25 },
            minWidth: 0,
            flex: 1,
          }}
        >
          <Box
            sx={{
              width: 40,
              height: 40,
              flexShrink: 0,
              display: { xs: 'none', sm: 'grid' },
              placeItems: 'center',
              borderRadius: '50%',
              color: 'primary.main',
              backgroundColor: alpha(theme.palette.primary.main, 0.09),
              border: `1px solid ${alpha(theme.palette.common.white, 0.82)}`,
              boxShadow: `0 8px 22px ${alpha(theme.palette.primary.main, 0.08)}`,
            }}
          >
            <EventIcon sx={{ fontSize: 22 }} />
          </Box>

          <Box sx={{ minWidth: 0, flexShrink: 0 }}>
            <Typography
              variant="h6"
              sx={{
                fontWeight: 700,
                lineHeight: 1.15,
                color: 'text.primary',
                whiteSpace: { xs: 'nowrap', sm: 'normal' },
                fontSize: { xs: '0.98rem', sm: '1.25rem' },
              }}
            >
              Календарь мероприятий
            </Typography>
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: { xs: 'none', sm: 'block' },
              }}
            >
              Расписание открытых мероприятий
            </Typography>
          </Box>

          <MonthNavigator
            sx={{
              ml: { xs: 'auto', sm: 1.5 },
              flexShrink: 0,
            }}
          >
            <MonthButton onClick={handlePrevMonth} aria-label="Предыдущий месяц" size="small">
              <ChevronLeftIcon fontSize="small" />
            </MonthButton>
            <Box
              sx={{
                minWidth: { xs: 112, sm: 150 },
                px: { xs: 0.7, sm: 1.2 },
                textAlign: 'center',
                position: 'relative',
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 700,
                  lineHeight: 1.15,
                  color: 'text.primary',
                  fontSize: { xs: '0.78rem', sm: '0.95rem' },
                  whiteSpace: 'nowrap',
                }}
              >
                {MONTHS[currentMonth]} {currentYear}
              </Typography>
              {loadingMonth === monthKey && (
                <CircularProgress
                  size={10}
                  thickness={5}
                  sx={{
                    position: 'absolute',
                    right: { xs: -1, sm: -2 },
                    top: '50%',
                    mt: '-5px',
                    color: 'primary.main',
                  }}
                />
              )}
            </Box>
            <MonthButton onClick={handleNextMonth} aria-label="Следующий месяц" size="small">
              <ChevronRightIcon fontSize="small" />
            </MonthButton>
          </MonthNavigator>
        </Box>

        <IconButton
          onClick={onClose}
          sx={{
            ml: { xs: 0.6, sm: 1.2 },
            width: 38,
            height: 38,
            flexShrink: 0,
            color: 'text.secondary',
            backgroundColor: alpha(theme.palette.common.white, 0.40),
            border: `1px solid ${alpha(theme.palette.common.white, 0.80)}`,
            '&:hover': {
              color: 'primary.main',
              backgroundColor: alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </StyledDialogTitle>

      <ContentSurface>

          {!isInitialLoading && !error && (
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                fontWeight: 500,
                whiteSpace: 'nowrap',
              }}
            >
              {cachedMonth?.totalEvents || 0} {(() => {
                const count = cachedMonth?.totalEvents || 0;
                const mod10 = count % 10;
                const mod100 = count % 100;
                if (mod10 === 1 && mod100 !== 11) return 'мероприятие';
                if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'мероприятия';
                return 'мероприятий';
              })()}
            </Typography>
          )}

        {error && !cachedMonth ? (
          <Alert
            severity="error"
            sx={{
              borderRadius: 2.5,
              backgroundColor: alpha(theme.palette.error.main, 0.055),
              border: `1px solid ${alpha(theme.palette.error.main, 0.12)}`,
            }}
          >
            {error}
          </Alert>
        ) : (
          <CalendarSurface
            sx={{
              opacity: isInitialLoading ? 0.64 : 1,
              transition: 'opacity 180ms ease',
            }}
          >
            <WeekdayGrid>
              {WEEKDAYS.map((day, index) => (
                <Typography
                  key={day}
                  variant="caption"
                  sx={{
                    textAlign: 'center',
                    fontWeight: 700,
                    fontSize: '0.68rem',
                    color: index >= 5
                      ? alpha(theme.palette.text.secondary, 0.58)
                      : 'text.secondary',
                    py: 0.35,
                  }}
                >
                  {day}
                </Typography>
              ))}
            </WeekdayGrid>

            {weeks.map((week, weekIndex) => {
              const selectedDayInWeek = week.find((day) => day && day === selectedDay);
              const weekHasSelected = Boolean(selectedDayInWeek);
              const selectedEvents = weekHasSelected ? getDayEvents(selectedDayInWeek) : [];

              return (
                <Box key={weekIndex}>
                  <CalendarGrid>
                    {week.map((day, dayIndex) => {
                      const dayEvents = day ? getDayEvents(day) : [];
                      const hasEvents = dayEvents.length > 0;
                      const isWeekend = dayIndex >= 5;

                      return (
                        <DayCell
                          key={`${weekIndex}-${dayIndex}`}
                          isToday={Boolean(day && isToday(day))}
                          hasEvents={hasEvents}
                          isSelected={Boolean(day && selectedDay === day)}
                          isWeekend={isWeekend}
                          onClick={() => day && handleDayClick(day)}
                          sx={{
                            visibility: day ? 'visible' : 'hidden',
                            pointerEvents: day ? 'auto' : 'none',
                          }}
                        >
                          {day && (
                            <>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: isToday(day) || selectedDay === day ? 750 : 500,
                                  fontSize: { xs: '0.78rem', sm: '0.84rem' },
                                }}
                              >
                                {day}
                              </Typography>

                              <EventDots>
                                {dayEvents.slice(0, MAX_DOTS).map((event, index) => {
                                  const palette = getEventTypePalette(event.type);
                                  return <EventDot key={`${event.id}-${index}`} color={palette.card} />;
                                })}
                                {dayEvents.length > MAX_DOTS && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: '0.52rem',
                                      lineHeight: 1,
                                      color: 'text.secondary',
                                      fontWeight: 700,
                                    }}
                                  >
                                    +{dayEvents.length - MAX_DOTS}
                                  </Typography>
                                )}
                              </EventDots>
                            </>
                          )}
                        </DayCell>
                      );
                    })}
                  </CalendarGrid>

                  <Collapse
                    in={weekHasSelected}
                    timeout={{ enter: 250, exit: 180 }}
                    unmountOnExit
                  >
                    {weekHasSelected && selectedEvents.length > 0 && (
                      <DayEventsPanel>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            gap: 1,
                            mb: 0.9,
                          }}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              sx={{
                                fontWeight: 750,
                                color: 'primary.main',
                                lineHeight: 1.2,
                              }}
                            >
                              {selectedDayInWeek} {MONTHS[currentMonth].toLowerCase()} {currentYear}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {pluralizeEvents(selectedEvents.length)}
                            </Typography>
                          </Box>
                          <IconButton
                            size="small"
                            onClick={() => setSelectedDay(null)}
                            aria-label="Свернуть события"
                            sx={{
                              width: 30,
                              height: 30,
                              color: 'text.secondary',
                              backgroundColor: alpha(theme.palette.common.white, 0.38),
                              border: `1px solid ${alpha(theme.palette.common.white, 0.70)}`,
                              '&:hover': {
                                color: 'primary.main',
                                backgroundColor: alpha(theme.palette.primary.main, 0.08),
                              },
                            }}
                          >
                            <CollapseIcon fontSize="small" />
                          </IconButton>
                        </Box>

                        <Box sx={{ maxHeight: 300, overflowY: 'auto', pr: 0.35 }}>
                          {selectedEvents.map((event) => {
                            const palette = getEventTypePalette(event.type);
                            const uiColor = palette.ui;

                            return (
                              <EventItem
                                key={event.id}
                                typeColor={uiColor}
                                onClick={() => handleEventClick(event.id)}
                                role="button"
                                tabIndex={0}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter' || e.key === ' ') {
                                    e.preventDefault();
                                    handleEventClick(event.id);
                                  }
                                }}
                              >
                                <Box
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.8,
                                    minWidth: 0,
                                  }}
                                >
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      flex: 1,
                                      minWidth: 0,
                                      fontWeight: 700,
                                      color: 'text.primary',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap',
                                    }}
                                  >
                                    {event.title}
                                  </Typography>
                                  <OpenInNewIcon
                                    sx={{
                                      flexShrink: 0,
                                      fontSize: 15,
                                      color: alpha(uiColor, 0.70),
                                    }}
                                  />
                                </Box>

                                <Box sx={{ display: 'flex', gap: 0.65, mt: 0.55, flexWrap: 'wrap' }}>
                                  {event.type_display && (
                                    <Chip
                                      label={event.type_display}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        borderRadius: 999,
                                        fontSize: '0.62rem',
                                        fontWeight: 650,
                                        bgcolor: alpha(palette.accent, 0.20),
                                        color: uiColor,
                                        border: `1px solid ${alpha(palette.accent, 0.22)}`,
                                      }}
                                    />
                                  )}
                                  {event.level_display && (
                                    <Chip
                                      label={event.level_display}
                                      size="small"
                                      sx={{
                                        height: 20,
                                        borderRadius: 999,
                                        fontSize: '0.62rem',
                                        color: 'text.secondary',
                                        backgroundColor: alpha(theme.palette.common.white, 0.32),
                                        border: `1px solid ${alpha(theme.palette.common.white, 0.68)}`,
                                      }}
                                    />
                                  )}
                                </Box>
                              </EventItem>
                            );
                          })}
                        </Box>
                      </DayEventsPanel>
                    )}
                  </Collapse>
                </Box>
              );
            })}
          </CalendarSurface>
        )}
      </ContentSurface>
    </StyledDialog>
  );
};

export default EventCalendarModal;
